import { useEffect, useState } from 'react';
import Switch from 'Components/Atoms/Switch/Switch';
import useWorkspaceSelected from 'Hooks/useWorkspaceSelected';
import { useTranslation } from 'react-i18next';
import Button from 'Components/Atoms/Button/Button';
import PropTypes from 'prop-types';
import { updateProjectWorkspacesSelected } from 'Services/user';
import { useErrors } from 'Components/Organisms/ErrorsProvider';
import { useQueryClient } from '@tanstack/react-query';

export default function InvoicesInvoicingScanDrawer(props) {
    const { onClose } = props;

    const { workspace } = useWorkspaceSelected();
    const { t } = useTranslation('invoices');
    const [processing, setProcessing] = useState(false);
    const { showUnknownError } = useErrors();
    const queryClient = useQueryClient();

    const [invoicingForceFromContact, setInvoicingForceFromContact] = useState(workspace?.invoicing_force_from_contact);
    const [senderAckEnabled, setSenderAckEnabled] = useState(workspace?.is_sender_ack_enabled);
    const [receiverAckEnabled, setReceiverAckEnabled] = useState(workspace?.is_receiver_ack_enabled);
    const [issuedAckEnabled, setIssuedAckEnabled] = useState(workspace?.is_issued_ack_enabled);

    useEffect(() => {
        setInvoicingForceFromContact(workspace?.invoicing_force_from_contact);
        setSenderAckEnabled(workspace?.is_sender_ack_enabled);
        setReceiverAckEnabled(workspace?.is_receiver_ack_enabled);
        setIssuedAckEnabled(workspace?.is_issued_ack_enabled);
    }, [workspace]);

    const submit = async () => {
        setProcessing(true);

        try {
            await updateProjectWorkspacesSelected(workspace.id, {
                invoicing_force_from_contact: invoicingForceFromContact,
                is_sender_ack_enabled: senderAckEnabled,
                is_receiver_ack_enabled: receiverAckEnabled,
                is_issued_ack_enabled: issuedAckEnabled,
            });
            await queryClient.invalidateQueries({ queryKey: ['project-workspace-selected'] });
        } catch (error) {
            showUnknownError(error);
        } finally {
            setProcessing(false);
            onClose();
        }
    };

    return (
        <div className="flex flex-col h-full">
            <div className="grow mt-8">
                <label className="flex items-start gap-4 mb-5">
                    <div className="py-2.5">
                        <Switch checked={invoicingForceFromContact} onCheckedChange={setInvoicingForceFromContact} />
                    </div>
                    <div>
                        <div className="font-semibold leading-tight text-pretty mb-1">
                            {t('invoicing-force-from-contact-title')}
                        </div>
                        <div className="text-xs opacity-50">{t('invoicing-force-from-contact-description')}</div>
                    </div>
                </label>
                <label className="flex items-start gap-4 mb-5">
                    <div className="py-2.5">
                        <Switch checked={receiverAckEnabled} onCheckedChange={setReceiverAckEnabled} />
                    </div>
                    <div>
                        <div className="font-semibold leading-tight text-pretty mb-1">
                            {t('receiver-ack-enabled-text')}
                        </div>
                        <div className="text-xs opacity-50">{t('receiver-ack-enabled-description')}</div>
                    </div>
                </label>
                <label className="flex items-start gap-4 mb-5">
                    <div className="py-2.5">
                        <Switch checked={senderAckEnabled} onCheckedChange={setSenderAckEnabled} />
                    </div>
                    <div>
                        <div className="font-semibold leading-tight text-pretty mb-1">
                            {t('sender-ack-enabled-text')}
                        </div>
                        <div className="text-xs opacity-50">{t('sender-ack-enabled-description')}</div>
                    </div>
                </label>
                <label className="flex items-start gap-4 mb-5">
                    <div className="py-2.5">
                        <Switch checked={issuedAckEnabled} onCheckedChange={setIssuedAckEnabled} />
                    </div>
                    <div>
                        <div className="font-semibold leading-tight text-pretty mb-1">
                            {t('issued-ack-enabled-text')}
                        </div>
                        <div className="text-xs opacity-50">{t('issued-ack-enabled-description')}</div>
                    </div>
                </label>
            </div>
            <div className="flex gap-2 py-5">
                <Button variant="primary" onClick={submit} isBusy={processing} type="buttom" className="grow">
                    {t('save')}
                </Button>
                <Button className="grow" onClick={onClose}>
                    {t('cancel')}
                </Button>
            </div>
        </div>
    );
}

InvoicesInvoicingScanDrawer.propTypes = {
    onClose: PropTypes.func.isRequired,
};
